import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";
import { createScrollDirectionState, resolveScrollDirection } from "@/lib/scrollDirection";
import {
  ProjectTopBar,
  type Project,
} from "@/components/v2/layout/ProjectTopBar";
import type { UserInfo, OrgInfo } from "@/components/v2/layout/UserProfileMenu";
import { ProjectTabs } from "@/components/v2/layout/ProjectTabs";
import type { ProjectTabId } from "@/pages/v2/projectTabs";

interface NavBarProps {
  currentProject: Project;
  projects?: Project[];
  user: UserInfo;
  activeTab: ProjectTabId;
  onProjectSelect?: (projectId: string) => void;
  onCreateProject?: () => void;
  onTabChange?: (tab: ProjectTabId) => void;
  onLogOut?: () => void;
  orgs?: OrgInfo[];
  currentOrgId?: string | null;
  onOrgSwitch?: (orgId: string) => void;
  /**
   * Whether to render the {@link ProjectTabs} strip below the top bar.
   * Defaults to `true` to preserve existing call sites; pass `false` for
   * surfaces that want only the project switcher chrome (e.g. brief, errors).
   */
  showTabs?: boolean;
  /**
   * 'project' (default): always render the project switcher dropdown.
   * 'home': render a static label when projects.length <= 1, dropdown otherwise.
   */
  variant?: 'home' | 'project';
  isAdmin?: boolean;
  /** Lock the project switcher to a static label (forwarded to ProjectTopBar). */
  lockProject?: boolean;
  /** Hide the project switcher + static label entirely (forwarded to ProjectTopBar). */
  hideProjectSwitcher?: boolean;
  /** Current project `status`, forwarded to {@link ProjectTabs} for status-aware tab visibility. */
  projectStatus?: string | null;
  className?: string;
}

export function NavBar({
  currentProject,
  projects,
  user,
  activeTab,
  onProjectSelect,
  onCreateProject,
  onTabChange,
  onLogOut,
  orgs,
  currentOrgId,
  onOrgSwitch,
  showTabs = true,
  variant = 'project',
  isAdmin = false,
  lockProject = false,
  hideProjectSwitcher = false,
  projectStatus,
  className,
}: NavBarProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  // Publish the nav bar's measured height to `:root` as `--anaka-nav-height`
  // so other fixed chrome (e.g. focused-mode ConversationShell) can offset
  // below the nav without overlapping it. ResizeObserver keeps the value live
  // as the tabs strip / project switcher reflow on viewport changes. Cleanup
  // removes the var so non-NavBar pages don't inherit a stale value.
  //
  // useLayoutEffect (not useEffect): the focused-mode ConversationShell paints
  // its `top: var(--anaka-nav-height, 7rem)` offset on the same frame as the
  // nav mounts. With post-paint `useEffect` the first frame uses the 7rem
  // fallback and then jumps when the real value lands — visible flicker.
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const sync = () => {
      const h = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--anaka-nav-height', `${h}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--anaka-nav-height');
    };
  }, []);

  // Scroll-direction hide/show: hide the nav when the user scrolls down past
  // a small threshold, reveal it on any upward movement or at the very top.
  // Shares `resolveScrollDirection` with `ConversationTranscript`'s container-
  // scroll hide (the scoping page's nav-collapse) so both hide/show mechanisms
  // are the same tested logic instead of two independently-drifting copies.
  // The near-top escape hatch is intentionally kept at `<10`, not the shared
  // function's `<=0` — NavBar's own threshold, preserved exactly.
  const [scrollHidden, setScrollHidden] = useState(false);
  const scrollStateRef = useRef(createScrollDirectionState());
  useEffect(() => {
    // Seed from the current position so a scroll-restored page doesn't
    // produce a false large-downward delta on the very first scroll event.
    scrollStateRef.current.lastFiredPos = window.scrollY;
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY < 10) {
        scrollStateRef.current.lastFiredPos = 0;
        setScrollHidden(false);
        return;
      }
      const direction = resolveScrollDirection(currentY, scrollStateRef.current, 5);
      if (direction === 'down') setScrollHidden(true);
      else if (direction === 'up') setScrollHidden(false);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // .anaka-app uses `container-type: inline-size` which (per CSS Containment
  // Level 2) makes it the containing block for position:fixed descendants.
  // Any `fixed` element inside .anaka-app would be fixed relative to
  // .anaka-app, not the viewport. To escape the containment context, the nav
  // is portaled to document.body so it can be truly viewport-fixed.
  //
  // An in-flow spacer (height = --anaka-nav-height) stays in the component
  // tree to reserve the nav's layout space, so callers need no per-page
  // padding-top. The ResizeObserver (useLayoutEffect above) sets the CSS var
  // before first paint, so the spacer is correctly sized on mount.
  const nav = (
    <div
      ref={rootRef}
      className={cn(
        "fixed top-0 left-0 right-0 z-30 flex flex-col",
        "transition-transform duration-300 ease-in-out",
        scrollHidden && "-translate-y-full",
        className,
      )}
    >
      <ProjectTopBar
        currentProject={currentProject}
        projects={projects}
        user={user}
        onProjectSelect={onProjectSelect}
        onCreateProject={onCreateProject}
        onLogOut={onLogOut}
        orgs={orgs}
        currentOrgId={currentOrgId}
        onOrgSwitch={onOrgSwitch}
        variant={variant}
        isAdmin={isAdmin}
        lockProject={lockProject}
        hideProjectSwitcher={hideProjectSwitcher}
      />
      {showTabs ? (
        <ProjectTabs
          projectUuid={currentProject.id}
          projectShortId={currentProject.short_id}
          activeTab={activeTab}
          onTabChange={onTabChange}
          projectStatus={projectStatus}
        />
      ) : null}
    </div>
  );

  return (
    <>
      {/* In-flow spacer — reserves the nav's measured height so content
          below starts correctly without per-page padding-top overrides. */}
      <div aria-hidden style={{ height: 'var(--anaka-nav-height, 4rem)', flexShrink: 0 }} />
      {typeof document !== 'undefined'
        ? createPortal(nav, document.body)
        : nav}
    </>
  );
}
